import {
  Box,
  Text,
  Group,
  RingProgress,
  useMantineTheme,
} from '@mantine/core';
import { useInterval } from '@mantine/hooks';
import { useState, useEffect, forwardRef, useCallback } from 'react';
import { IconPlayerPlay, IconPlayerPause, IconRefresh } from '@tabler/icons-react';
import { Button } from '@/components/ui';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface CountdownTimerProps {
  duration: number; // in seconds
  autoStart?: boolean;
  onComplete?: () => void;
  onTick?: (remaining: number) => void;
  showControls?: boolean;
  showProgress?: boolean;
  format?: 'mm:ss' | 'hh:mm:ss' | 'seconds';
  size?: 'sm' | 'md' | 'lg';
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  label?: string;
}

const formatTime = (seconds: number, format: CountdownTimerProps['format']): string => {
  if (format === 'seconds') return `${seconds}s`;

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (format === 'hh:mm:ss' || hrs > 0) {
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const CountdownTimer = forwardRef<HTMLDivElement, CountdownTimerProps>(
  (
    {
      duration,
      autoStart = false,
      onComplete,
      onTick,
      showControls = true,
      showProgress = true,
      format = 'mm:ss',
      size = 'md',
      gradientVariant = 'primary',
      label,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const [remaining, setRemaining] = useState(duration);
    const [isRunning, setIsRunning] = useState(autoStart);

    const sizes = {
      sm: { ring: 80, thickness: 6, fontSize: 'md' },
      md: { ring: 120, thickness: 8, fontSize: 'xl' },
      lg: { ring: 160, thickness: 10, fontSize: '2rem' },
    };

    const currentSize = sizes[size];
    const progress = ((duration - remaining) / duration) * 100;

    const tick = useCallback(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          setIsRunning(false);
          onComplete?.();
          return 0;
        }
        const newValue = prev - 1;
        onTick?.(newValue);
        return newValue;
      });
    }, [onComplete, onTick]);

    const interval = useInterval(tick, 1000);

    useEffect(() => {
      if (isRunning) {
        interval.start();
      } else {
        interval.stop();
      }
      return interval.stop;
    }, [isRunning]);

    const handleStart = () => setIsRunning(true);
    const handlePause = () => setIsRunning(false);
    const handleReset = () => {
      setIsRunning(false);
      setRemaining(duration);
    };

    return (
      <Box ref={ref} ta="center">
        {label && (
          <Text size="sm" fw={500} mb="sm" style={accessibilityStyles}>
            {label}
          </Text>
        )}

        {showProgress ? (
          <RingProgress
            size={currentSize.ring}
            thickness={currentSize.thickness}
            roundCaps
            sections={[
              {
                value: 100 - progress,
                color: remaining === 0 ? 'red' : gradient?.from || 'blue',
              },
            ]}
            label={
              <Text
                size={currentSize.fontSize as any}
                fw={700}
                ta="center"
                style={{
                  ...accessibilityStyles,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatTime(remaining, format)}
              </Text>
            }
          />
        ) : (
          <Text
            size={currentSize.fontSize as any}
            fw={700}
            style={{
              ...accessibilityStyles,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatTime(remaining, format)}
          </Text>
        )}

        {showControls && (
          <Group justify="center" mt="md" gap="xs">
            {isRunning ? (
              <Button
                size="xs"
                variant="light"
                leftSection={<IconPlayerPause size={14} />}
                onClick={handlePause}
              >
                Pause
              </Button>
            ) : (
              <Button
                size="xs"
                gradientVariant={gradientVariant}
                leftSection={<IconPlayerPlay size={14} />}
                onClick={handleStart}
                disabled={remaining === 0}
              >
                {remaining === duration ? 'Start' : 'Resume'}
              </Button>
            )}
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconRefresh size={14} />}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Group>
        )}
      </Box>
    );
  }
);

CountdownTimer.displayName = 'CountdownTimer';

export default CountdownTimer;
